import "./Checkout.scss";

import { captureException, captureMessage, withScope } from "@sentry/react";
import { useAtom } from "jotai";
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CheckoutDueNow } from "@/components/Checkout/Steps/Checkout/CheckoutDueNow/CheckoutDueNow";
import { Step } from "@/components/Checkout/Steps/Step";
import { PortalTopBanners } from "@/components/Interface/Alert/PortalTopBanners";
import { Button, ButtonLink } from "@/components/Interface/Button/Button";
import { StepFlowNavigation } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowNavigation";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import {
  PaymentMeanSection,
  PaymentProviderObject,
} from "@/components/Portal/DeviceManagement/Roaming/CheckoutRoamingOption/PaymentMeanSection/PaymentMeanSection";
import { getHeader } from "@/components/Portal/DeviceManagement/Roaming/roamingUtils";
import { LightboxWrapper } from "@/components/Portal/Documents/PaymentMethods/LightboxWrapper/LightboxWrapper";
import { useGetFlatPricesAndName } from "@/hooks/useDataContextHooks";
import { useHandleCheckoutError } from "@/hooks/useHandleCheckoutError";
import { useHandleError } from "@/hooks/useHandleError";
import { transformPaymentMean } from "@/hooks/usePaymentMode";
import { usePreviousWizardPath } from "@/hooks/usePreviousWizardPath";
import { useQueryParam } from "@/hooks/useQueryParam";
import { ErrorType } from "@/mutator/frontendApiInstance";
import { DataContext } from "@/provider/DataContextProvider";
import { FullScreenLoadingContext } from "@/provider/FullScreenLoadingProvider/FullScreenLoadingProvider";
import {
  useAddContract,
  useAddOption,
  useCreateTransaction,
  useGetCustomer,
  useGetPaymentMean,
  useGetSimCustomerActions,
  useGetUpdatedBasket,
  usePayTransaction,
  useSetEntryCustomData,
} from "@/services/api";
import {
  BasketEntry,
  CreateTransactionDatatransOrderType,
  DatatransPaymentResponse,
  DatatransPaymentResponseStatus,
  SignUpResponse,
  SimCustomerInfoCreditClass,
} from "@/services/model";
import { basketIsUpdatedAtom } from "@/utils/atoms";
import { ServiceIDs } from "@/utils/constants";
import { canAddESim, canOnlyBookData } from "@/utils/customerUtils";
import {
  getEntry,
  hasBasketEntryWithVoiceOption,
  isValidSimAlias,
  withPreservedSimAlias,
} from "@/utils/dataContextHelpers";
import {
  get3DsBrowserInfo,
  // TODO: Comment back in during 1393
  // isCardholderNameMissing,
} from "@/utils/lightboxHelpers";
import { getDefaultAliasKey } from "@/utils/productMatrix";
import {
  isFlatHomeTariff,
  isFlatMobileInternationalCallsIncluded,
} from "@/utils/tariffUtils";

import { CheckoutBillingPeriodSwitch } from "./CheckoutBillingPeriodSwitch/CheckoutBillingPeriodSwitch";
import { Summary } from "./Summary/Summary";
import { TermsAgreement } from "./TermsAgreement/TermsAgreement";

export interface FinalFormProps extends PaymentProviderObject {
  terms: boolean;
}

export const Checkout: FC = () => {
  const [basketIsUpdated, setBasketIsUpdated] = useAtom(basketIsUpdatedAtom);
  const [isSubmittingCheckout, setIsSubmittingCheckout] = useState(false);
  const [showSummaryErrors, setShowSummaryErrors] = useState(false);
  const [isBillingPeriodSwitching, setIsBillingPeriodSwitching] =
    useState(false);
  // setEntryCustomData below runs before any of the addContract/transaction
  // mutations start - without this, that first await leaves the button
  // enabled and spinner-less while the click is already being handled.
  const [isPersistingAlias, setIsPersistingAlias] = useState(false);
  const { dataContext, setDataContext } = useContext(DataContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleError = useHandleError();
  const { setFullScreenLoading } = useContext(FullScreenLoadingContext);

  useEffect(() => {
    if (isSubmittingCheckout) {
      setFullScreenLoading(true, t("Onboarding.sections.checkout.loading"));
    }
  }, [setFullScreenLoading, t, isSubmittingCheckout]);

  const { data: customerData } = useGetCustomer();
  const canBookVoiceOption = !(customerData && canOnlyBookData(customerData));
  const entry = getEntry(dataContext) as BasketEntry;

  const { data: simCustomerActions } = useGetSimCustomerActions();

  const serviceId = entry?.options?.[0]?.product?.id || entry?.product?.id;

  const showInternationalCallPermissionHint =
    isFlatMobileInternationalCallsIncluded(serviceId as ServiceIDs) &&
    simCustomerActions?.creditClass === SimCustomerInfoCreditClass.CreditClassC;

  const previousPath = usePreviousWizardPath(4);

  const hasPaymentSuccessParam = useQueryParam("payment")?.[0] === "success";
  const isEsim = entry?.esim ?? false;
  const hasVoiceOption = hasBasketEntryWithVoiceOption(dataContext);

  const {
    watch,
    register,
    formState: { errors },
    control,
  } = useForm<FinalFormProps>({
    defaultValues: { ...(dataContext.checkout || {}) },
  });
  const watchedPaymentProvider = watch("paymentProvider");
  const watchedTerms = watch("terms");
  const { isInvoice, isCreditCard, maskedCardNumberString, cardType } =
    transformPaymentMean(useGetPaymentMean().data);

  const { onceTotal } = useGetFlatPricesAndName();

  const oneTimeAmount = dataContext.basket?.total?.oneTime?.amount;
  const oneTimeAmountIsZero = onceTotal !== undefined && onceTotal <= 0;

  const isFlatHome = isFlatHomeTariff(serviceId);

  // Payment method is always mandatory for all customers
  // Can only skip if already have a saved payment method (invoice or credit card)
  const canSkipPaymentWithoutPaymentMean = isInvoice || isCreditCard;

  useGetUpdatedBasket(
    dataContext.basket?.id || -1,
    { updateBasket: true },
    {
      query: {
        enabled: !!dataContext.basket?.id,
        onSuccess: (fetchedBasket) => {
          // We need to remove the voice option for users who are not allowed to
          // select it, but did so as anonymous users before logging in.
          // @TODO: Still relevant?
          if (!canBookVoiceOption && hasVoiceOption) {
            const { selectedOption } = dataContext;

            addOption({
              basketId: fetchedBasket.id as number,
              uuid: fetchedBasket.entries[0].uuid as string,
              params: {
                optionId: selectedOption?.data as number,
              },
            });
          } else {
            setDataContext((prev) => ({
              ...prev,
              basket: withPreservedSimAlias(prev.basket, fetchedBasket),
            }));
            setBasketIsUpdated(true);
          }
        },
      },
    },
  );

  const { mutate: addOption } = useAddOption({
    mutation: {
      onSuccess: (data) => {
        setDataContext((prev) => ({
          ...prev,
          basket: withPreservedSimAlias(prev.basket, data),
        }));
        setBasketIsUpdated(true);
      },
    },
  });

  const { mutateAsync: setEntryCustomData } = useSetEntryCustomData();
  const aliasPersistLock = useRef(false);

  // TODO: Comment back in during 1393
  // const [cardholderName, setCardholderName] = useState("");

  const {
    mutate: createTransaction,
    data: datatransRequestDocument,
    isLoading: isCreatingTransaction,
  } = useCreateTransaction();

  useHandleCheckoutError(oneTimeAmountIsZero);

  const handleSuccess = useCallback(async () => {
    // Clear the basket and info entered during the onboarding
    // and reset the allowedMaxOnboardingStep, so the user cannot navigate
    // from step 1 to step 5 in the next onboarding process.
    setDataContext((prev) => ({
      allowedMaxOnboardingStep: undefined,
      isEmailVerified: prev.isEmailVerified,
    }));
    navigate("/portal");
    setFullScreenLoading(false);
  }, [navigate, setFullScreenLoading, setDataContext]);

  // If the user has just paid (query param is = success ), we need to handle the success
  useEffect(() => {
    if (!isSubmittingCheckout && hasPaymentSuccessParam) {
      setIsSubmittingCheckout(true); // To prevent double checkout on re-rendering
      handleSuccess();
    }
  }, [
    hasPaymentSuccessParam,
    dataContext?.basket,
    isSubmittingCheckout,
    handleSuccess,
  ]);

  // Both usePayTransaction and useAddContract share the same behavior onSuccess and onError
  const transactionMutationOptions = {
    onSuccess: (data: DatatransPaymentResponse | SignUpResponse) => {
      if ("signUpId" in data) {
        handleSuccess();
      } else {
        if (data.status === DatatransPaymentResponseStatus.OK) {
          handleSuccess();
        } else {
          setIsSubmittingCheckout(false);
          handleError(
            "Payment failure",
            `${t("Error.payment.fail")} ${t("Error.payment.pleaseCheckData")}`,
          );
          withScope(function (scope) {
            scope.setFingerprint(["/contract/serviceId/options"]);
            scope.setTag("section", "portal");
            scope.setTag("endpoint", "addOptionToContract");
            scope.setContext("paymentContext", {
              isInvoice,
              isCreditCard,
              maskedCardNumberString,
              cardType,
            });
            captureMessage("Payment failure");
          });
        }
      }
    },
    onError: (err: ErrorType<unknown>) => {
      setFullScreenLoading(false);
      // If it fails we report the error in sentry
      handleError(err);
      withScope(function (scope) {
        scope.setFingerprint(["/datatrans/pay"]);
        scope.setTag("section", "portal");
        scope.setTag("endpoint", "payTransaction");
        scope.setContext("paymentContext", {
          basketId: dataContext?.basket?.id,
          isInvoice,
          isCreditCard,
          maskedCardNumberString,
          cardType,
        });
        captureException(err);
      });
    },
  };

  // This is used to pay transactions with a saved credit card
  const { mutate: payTransaction } = usePayTransaction({
    mutation: transactionMutationOptions,
  });

  const {
    mutate: addContract,
    isLoading: isAddContractLoading,
    isSuccess: isAddContractDone,
  } = useAddContract({
    mutation: transactionMutationOptions,
  });
  const addContractCallback = useCallback(
    (basketId: number) => {
      addContract({
        data: {
          header: getHeader(),
          basketId,
        },
        // Auto-renewal is always on; users can pause anytime in SIM management.
        params: { autoRenew: true },
      });
    },
    [addContract],
  );

  const handleSubmit = async () => {
    if (isSubmittingCheckout || aliasPersistLock.current) {
      return;
    }

    const basketId = dataContext?.basket?.id;
    // If we don't have a basket id, we can't checkout.
    if (basketId === undefined) {
      captureMessage("Wanted to submit undefined basket id!");
      handleError("Wanted to submit undefined basket id!");

      return;
    }

    if (oneTimeAmount === undefined) {
      captureMessage("Wanted to charge undefined oneTime amount");
      handleError("Wanted to charge undefined oneTime amount");

      return;
    }

    // Prefer the live input so an in-progress edit is not lost to a stale
    // basket (blur + continue can race, and basket refreshes can drop SimAlias).
    const typedAlias =
      (
        document.getElementById("checkout-sim-alias") as HTMLInputElement | null
      )?.value?.trim() || "";
    const basketAlias =
      (entry?.customData?.["SimAlias"] as string)?.trim() || "";
    const productId = entry?.options?.[0]?.product?.id || entry?.product?.id;
    const aliasKey = getDefaultAliasKey(productId);
    const defaultAlias = aliasKey
      ? t(`Onboarding.sections.checkout.summary.${aliasKey}` as any).trim()
      : "";

    const aliasToPersist = isValidSimAlias(typedAlias)
      ? typedAlias
      : isValidSimAlias(basketAlias)
        ? basketAlias
        : defaultAlias;

    if (!isValidSimAlias(aliasToPersist) || !entry?.uuid) {
      setShowSummaryErrors(true);
      document
        .getElementById("checkout-layout")
        ?.scrollIntoView?.({ behavior: "smooth" });

      return;
    }

    aliasPersistLock.current = true;
    setIsPersistingAlias(true);

    try {
      const updatedBasket = await setEntryCustomData({
        basketId,
        uuid: entry.uuid,
        data: { SimAlias: aliasToPersist },
      });
      setDataContext((prev) => ({
        ...prev,
        basket: withPreservedSimAlias(prev.basket, updatedBasket),
      }));
    } catch (err) {
      handleError(err);
      setShowSummaryErrors(true);
      document
        .getElementById("checkout-layout")
        ?.scrollIntoView?.({ behavior: "smooth" });

      return;
    } finally {
      aliasPersistLock.current = false;
      setIsPersistingAlias(false);
    }

    // Save current context
    setDataContext((prev) => ({
      ...prev,
      checkout: {},
    }));

    // If we have a saved payment mean or the amount is zero (but not for Flat Home)...
    if (canSkipPaymentWithoutPaymentMean) {
      setIsSubmittingCheckout(true); // To prevent double checkout on re-rendering
      if (isCreditCard) {
        // We create the contract by paying the transaction
        payTransaction({
          params: {
            datatransOrderType:
              CreateTransactionDatatransOrderType.AddContractToCustomer,
            basketId,
            autorenew: true,
          },
        });
      }
      // Contract should be added directly if we are in the "add device checkout".
      // Or if we have a sumIsZero transaction WITHOUT specified paymentMean (but not for Flat Home)!
      else if (!isAddContractDone && !isAddContractLoading) {
        addContractCallback(basketId);
      }
    } else {
      // Else we create a transaction, which will in turn open the Lightbox, allow the user to enter payment information
      // and call addContract backend-side if the payment was successful.
      createTransaction({
        params: {
          datatransOrderType:
            CreateTransactionDatatransOrderType.AddContractToCustomer,
          basketId,
          autorenew: true,
          // TODO: Comment back in during 1393
          // cardholderName,
          ...get3DsBrowserInfo(),
        },
      });
    }
  };

  const termsAccepted = !!watchedTerms;

  // Payment method is always mandatory for all customers
  const paymentMethodRequired =
    !watchedPaymentProvider && !(isInvoice || isCreditCard);

  // Disable continue if...
  const continueIsDisabled =
    (isEsim && !canAddESim(simCustomerActions)) ||
    isAddContractLoading ||
    isAddContractDone ||
    isSubmittingCheckout ||
    isPersistingAlias ||
    !termsAccepted ||
    // or payment method is required (always mandatory for all customers)
    paymentMethodRequired ||
    // or a billing-period switch is still being persisted
    isBillingPeriodSwitching;
  // TODO: Comment back in during 1393
  // || isCardholderNameMissing(watchedPaymentProvider, cardholderName)

  return (
    <Step
      id="onboarding-final"
      headline={t("Onboarding.sections.checkout.title")}
      subheadline={t(
        `Onboarding.sections.checkout.${
          isAddContractLoading || isAddContractDone || isSubmittingCheckout
            ? "subtitleWhileHandling"
            : "subtitle"
        }`,
      )}
      navigation={
        <StepFlowNavigation
          className="mt-6 tablet:mt-10"
          primaryButton={
            <Button
              dataTestid="continue-button"
              variant="primary"
              disabled={continueIsDisabled}
              loading={isPersistingAlias}
              fullWidth
              onClick={handleSubmit}
            >
              {t("Common.label.complete")}
            </Button>
          }
          secondaryButton={
            <ButtonLink
              variant="secondary"
              to={previousPath ?? "."}
              disabled={!previousPath}
              fullWidth
            >
              {t("Common.label.back")}
            </ButtonLink>
          }
        />
      }
    >
      <PortalTopBanners placement="checkout" />
      {isCreatingTransaction ||
      isAddContractLoading ||
      isAddContractDone ||
      isSubmittingCheckout ? (
        // Don't render checkout screen if we're still loading something or handling the successful payment,
        // as this leads to errors in the rendered children (e.g. PaymentMeanSection without a basket)
        <LoadingSpinner />
      ) : (
        <>
          <TwoColumnLayout
            id="checkout-layout"
            className="mt-10"
            independentColumns
            rows={[
              {
                left: (
                  <Summary
                    isFlatHome={isFlatHome}
                    showErrors={showSummaryErrors}
                    isInternationalCallsRestricted={
                      showInternationalCallPermissionHint
                    }
                  />
                ),
                right: <CheckoutDueNow />,
              },
              {
                left: (
                  <section id="payment-section">
                    {dataContext.basket !== undefined && basketIsUpdated ? (
                      <>
                        <PaymentMeanSection
                          isInvoice={isInvoice}
                          isCreditCard={isCreditCard}
                          cardType={cardType}
                          maskedCardNumberString={maskedCardNumberString}
                          control={control}
                          enableShowCurrent={!!(isInvoice || isCreditCard)}
                          sumIsZero={oneTimeAmountIsZero}
                        />
                        <LightboxWrapper
                          paymentProvider={watchedPaymentProvider}
                          transactionId={datatransRequestDocument?.refNo}
                          sign={datatransRequestDocument?.sign}
                          enabled={datatransRequestDocument !== undefined}
                          // TODO: Comment back in during 1393
                          // cardholderName={cardholderName}
                          // onCardholderNameChange={setCardholderName}
                        />
                      </>
                    ) : (
                      <LoadingSpinner />
                    )}
                  </section>
                ),
                right: (
                  <CheckoutBillingPeriodSwitch
                    disabled={!basketIsUpdated}
                    onSwitchingChange={setIsBillingPeriodSwitching}
                  />
                ),
              },
            ]}
          />
          <div id="terms-agreement" className="mt-6">
            <TermsAgreement<FinalFormProps>
              register={register}
              errors={errors}
              name="terms"
            />
          </div>
        </>
      )}
    </Step>
  );
};
